import { Checkbox, Image } from "antd";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import axiosInstance from "../../../variables/api/axiosInstance";
import '../../../assets/styles/quittanceIndemnite.css';
import barre from "../../../assets/img/barre.png";
import entete from '../../../assets/img/entete_pool.png'
import entfoot from "../../../assets/img/footer.png";
import { frenchDate, frenchDateOnly, numberToWordsFrench } from "../../../variables/functions";
import { displayNumbers } from "../../../variables/numbersFunctions";
import { getCompagnieLogo } from "../../../hooks/useFilesHooks";
import { DocumentFooter } from "../../../components/docs/DocumentFooter";
import TableauDossiersGroupe from "./TableauDossiersGroupe";
import SignatairesVisa from "./SignatairesVisa";
import { TYPE_DOC_SIGNATAIRE } from "../../../hooks/useSignataires";
import { motifReglementAffiche } from "./groupeReglement";

const styles = {
    fontFamily: "Calibri, Arial, sans-serif",
    fontSize: "12px",
    color: "black",
    stamp: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%) rotate(-45deg)",
        color: "red",
        fontSize: "72px",
        fontWeight: "bold",
        opacity: 0.5,
        zIndex: 1000,
        pointerEvents: "none",
        border: "5px solid red",
        padding: "20px",
        textAlign: "center",
    },
};

// Ajouter les styles d'impression
const printStyles = `
@media print {
    @page {
        size: A4 portrait;
        margin: 10mm;
    }
    
    .quittance-container {
        width: 100%;
    }

    /* En-tête et pied de page RÉPÉTÉS sur CHAQUE page à l'impression : le navigateur re-dessine
       automatiquement <thead>/<tfoot> en haut/bas de chaque page. Quand le contenu déborde, il passe
       en page 2 AVEC le même en-tête et le même pied (au lieu de tout décaler brutalement). */
    .quittance-print-table {
        width: 100%;
        border-collapse: collapse;
    }
    .quittance-thead { display: table-header-group; }
    .quittance-tfoot { display: table-footer-group; }

    /* Remplit la page pour garder le pied en bas sur une page unique, SANS forcer un débordement. */
    .quittance-content-wrapper {
        min-height: 190mm;
    }
    
    * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }
    
    body {
        margin: 0;
        padding: 0;
    }
}
`;


const QuittanceIndemnite = React.forwardRef((props, ref) => {
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
    // Bloc visa (compagnies) + signature du DG ajoutée à la demande et PERSISTÉE (clé = codeId règlement).
    const [dgDemande, setDgDemande] = useState(false);
    const isCompany = props?.sinistre?.contrat?.organisme?.typeOrganisme !== "POOL";
    const cleDocument = props?.decaissement?.codeId;

    useEffect(() => {
        if (!isCompany || !cleDocument) return;
        axiosInstance.get(`/api/document-signature-dg/${cleDocument}`)
            .then(({ data }) => setDgDemande(!!data?.dgDemande))
            .catch(() => setDgDemande(false));
    }, [isCompany, cleDocument]);

    const toggleDgDemande = (checked) => {
        setDgDemande(checked);
        axiosInstance.put(`/api/document-signature-dg/${cleDocument}`, { dgDemande: checked })
            .catch(() => setDgDemande(!checked));
    };

    useEffect(() => {
        if (props?.sinistre?.contrat?.organisme?.typeOrganisme !== "POOL") {
            getCompagnieLogo(props?.sinistre?.contrat?.organisme?.code)
                .then(async (response) => {
                    const imageBytes = await response.arrayBuffer();
                    var blob = new Blob([imageBytes], { type: "image/png" });
                    var imageUrl = URL.createObjectURL(blob);
                    setLogoPreviewUrl(imageUrl);
                    var file = new File(
                        [blob],
                        props?.contrat?.organisme?.logo?.split("/")[1],
                        {
                            type: "image/png",
                        }
                    );
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    }, []);

    // ── Victime décédée vs blessée ───────────────────────────────────────────────
    // Pour une victime DÉCÉDÉE payée via un ayant droit : le représentant (ayant droit)
    // signe la quittance, et la victime (de cujus) figure sous « Agissant au nom des
    // ayants droits de : ». Pour une victime blessée : comportement actuel inchangé.
    const beneficiaire = props?.decaissement?.beneficiaire;
    const representant = props?.decaissement?.representant;
    // Victime du sinistre correspondant au bénéficiaire (sa personne), pour lire decesSinistre.
    const victimeCorrespondante = props?.sinistre?.victimes?.find(
        (v) => v?.personne?.codeId && v?.personne?.codeId === beneficiaire?.codeId
    );
    // Décès reconnu soit par la victime correspondante, soit par le flag « ayant droit » du règlement.
    const victimeDecedee = victimeCorrespondante?.decesSinistre === true
        || props?.decaissement?.representantAyantDroit === true;
    // Nom de la victime (de cujus) = la personne bénéficiaire (= victime côté backend).
    const nomVictimeDeCujus = beneficiaire?.prenom
        ? `${beneficiaire?.nom} ${beneficiaire?.prenom}`
        : beneficiaire?.nom;
    // Nom affiché « Je soussigné » en cas standard (établissement le cas échéant, sinon bénéficiaire).
    const nomBeneficiaireStd = props?.decaissement?.beneficiaireMoral
        ? props?.decaissement?.beneficiaireMoral?.nom
        : nomVictimeDeCujus;
    // Cas décès avec représentant : on bascule le libellé. (Pas pour un paiement à établissement.)
    const estCasDecesAvecRepresentant = victimeDecedee
        && !props?.decaissement?.beneficiaireMoral && !!representant;

    // Règlement groupé/multiple : quand plusieurs dossiers sont réglés ensemble,
    // les cellules propres à UN dossier (n° sinistre/police, survenance, assuré,
    // adresse du souscripteur) sont remplacées par le tableau récapitulatif.
    const lignesGroupe = props?.decaissement?.lignes || [];
    const isGroupe = !!props?.decaissement?.isReglementMultiple && lignesGroupe.length > 0;
    const multiDossiers = (props?.decaissement?.dossiers?.length || 0) > 1;

    return (
        <>
            {/* Contrôle masqué à l'impression (@media print). Compagnies uniquement. */}
            {isCompany && cleDocument && (
                <div className="dg-toolbar" style={{ margin: "0 0 10px", padding: "8px 12px", background: "#f6f7fb", borderRadius: 8, border: "1px solid #eef0f4" }}>
                    <style>{`@media print { .dg-toolbar { display: none !important; } }`}</style>
                    <Checkbox checked={dgDemande} onChange={(e) => toggleDgDemande(e.target.checked)}>
                        Ajouter la signature du <b>Directeur Général</b> (persistée à chaque réimpression)
                    </Checkbox>
                </div>
            )}
        <div className="quittance-container" ref={ref} style={{ fontFamily: "Calibri, Arial, sans-serif" }}>
            <style>{printStyles}</style>
            <table className="quittance-print-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead className="quittance-thead"><tr><td style={{ padding: 0 }}>
            <div style={{ flexShrink: 0 }}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "0px 0px",
                    }}
                >
                    {props?.sinistre?.contrat?.organisme?.typeOrganisme !== "POOL" && (
                        <Image
                            src={logoPreviewUrl}
                            style={{
                                width: "100%",
                                height: "105px",
                            }}
                        />
                    )}

                    <Image
                        src={entete}
                        style={{
                            width: "100%",
                            height: "105px",
                        }}
                    />
                </div>
                <Image
                    src={barre}
                    style={{
                        width: "109%",
                        height: "5px",
                        paddingTop: "-10px",
                    }}
                />
            </div>
                </td></tr></thead>
                <tfoot className="quittance-tfoot"><tr><td style={{ padding: 0 }}>
                    <DocumentFooter />
                </td></tr></tfoot>
                <tbody><tr><td style={{ padding: 0, verticalAlign: "top" }}>
            <div className="quittance-content-wrapper" style={{ padding: "20px 40px" }}>
                <div className="quittance-text-block" style={{
                    width: "100%", borderCollapse: "collapse", fontFamily: "Calibri, Arial, sans-serif", fontSize: "22px",
                    WebkitTextStroke: "0.5px black",
                }}>
                    <p style={{ textAlign: "center", fontSize: "22px", border: "1px solid black" }}><b>QUITTANCE</b></p>
                </div>
                <br />
                {multiDossiers ? (
                    /* Règlement multiple : pas d'infos d'UN seul sinistre en tête —
                       le détail par dossier figure dans le tableau récapitulatif. */
                    <table className="quittance-data-table" border={1} style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Calibri, Arial, sans-serif", fontSize: "12px" }}>
                        <tr>
                            <td style={{ padding: "6px", fontWeight: "bold", textAlign: "center", border: "1px solid black" }}>
                                {props?.sinistre?.contrat?.organisme?.raisonSociale?.toUpperCase()}
                            </td>
                            <td style={{ padding: "6px", fontWeight: "bold", textAlign: "center", border: "1px solid black" }}>
                                PAIEMENT EN FCFA
                            </td>
                            <td style={{ padding: "6px", fontWeight: "bold", textAlign: "center", border: "1px solid black" }}>
                                NOMBRE DE DOSSIERS
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: "6px", textAlign: "center", border: "1px solid black" }}>SIEGE</td>
                            <td style={{ padding: "6px", textAlign: "center", border: "1px solid black" }}>
                                {displayNumbers(Math.trunc(props?.decaissement?.montantGlobal || 0))}
                            </td>
                            <td style={{ padding: "6px", textAlign: "center", border: "1px solid black" }}>
                                {props?.decaissement?.dossiers?.length}
                            </td>
                        </tr>
                    </table>
                ) : (
                    <table className="quittance-data-table" border={1} style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Calibri, Arial, sans-serif", fontSize: "12px" }}>
                        <tr>
                            <td style={{ padding: "6px", fontWeight: "bold", textAlign: "center", border: "1px solid black" }}>
                                {props?.sinistre?.contrat?.organisme?.raisonSociale?.toUpperCase()}
                            </td>
                            <td style={{ padding: "6px", fontWeight: "bold", textAlign: "center", border: "1px solid black" }}>
                                N° SINISTRE
                            </td>
                            <td style={{ padding: "6px", fontWeight: "bold", textAlign: "center", border: "1px solid black" }}>
                                N° POLICE
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: "6px", textAlign: "center", border: "1px solid black" }}>SIEGE</td>
                            <td style={{ padding: "6px", textAlign: "center", border: "1px solid black" }}>
                                {props?.sinistre?.numSinistre || props?.sinistre?.numeroSinistreM}
                            </td>
                            <td style={{ padding: "6px", textAlign: "center", border: "1px solid black" }}>
                                {props?.sinistre?.contrat?.numeroPoliceP
                                    ? props?.sinistre?.contrat?.numeroPoliceP
                                    : props?.sinistre?.contrat?.numeroPoliceM}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: "6px", fontWeight: "bold", textAlign: "center", border: "1px solid black" }}>
                                PAIEMENT EN FCFA
                            </td>
                            <td style={{ padding: "6px", fontWeight: "bold", textAlign: "center", border: "1px solid black" }}>
                                SURVENU LE
                            </td>
                            <td style={{ padding: "6px", fontWeight: "bold", textAlign: "center", border: "1px solid black" }}>
                                ASSURE
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: "6px", textAlign: "center", border: "1px solid black" }}>
                                {displayNumbers(Math.trunc(props?.decaissement?.montantGlobal))}
                            </td>
                            <td style={{ padding: "6px", textAlign: "center", border: "1px solid black" }}>
                                {frenchDate(props?.sinistre?.dateSinistre)}
                            </td>
                            <td style={{ padding: "6px", textAlign: "center", border: "1px solid black" }}>
                                {props?.sinistre?.contrat?.souscripteur?.prenom
                                    ? `${props?.sinistre?.contrat?.souscripteur?.nom} ${props?.sinistre?.contrat?.souscripteur?.prenom}`
                                    : props?.sinistre?.contrat?.souscripteur?.nom}
                            </td>
                        </tr>
                    </table>
                )}
                <br />
                <div className="quittance-text-block" style={{ fontSize: "12px", lineHeight: "1.6", fontFamily: "Calibri, Arial, sans-serif" }}>
                    <p style={{ marginBottom: "8px", fontSize: "14px" }}>
                        Je soussigné :{" "}
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
                            {/* Victime DÉCÉDÉE → le représentant (ayant droit) signe ; sinon
                                bénéficiaire (établissement le cas échéant, comme l'ordre de dépense). */}
                            {estCasDecesAvecRepresentant ? representant : nomBeneficiaireStd}
                        </span>
                    </p>
                    {estCasDecesAvecRepresentant ? (
                        <p style={{ marginBottom: "8px", fontSize: "14px" }}>
                            Agissant au nom des ayants droits de :{" "}
                            <span style={{ fontWeight: "bold", fontSize: "14px" }}>
                                {nomVictimeDeCujus}
                            </span>
                        </p>
                    ) : (
                        props?.decaissement?.representant && (
                            <p style={{ marginBottom: "8px", fontSize: "14px" }}>
                                Représenté par :{" "}
                                <span style={{ fontWeight: "bold", fontSize: "14px" }}>
                                    {props?.decaissement?.representant}
                                </span>
                            </p>
                        )
                    )}
                    {/* Adresse du souscripteur = info d'UN dossier : masquée pour un règlement multiple. */}
                    {!multiDossiers && (
                        <p style={{ marginBottom: "8px", fontSize: "14px" }}>
                            Demeurant à :{" "}
                            <span style={{ fontWeight: "bold", fontSize: "14px" }}>
                                {props?.sinistre?.contrat?.souscripteur?.adresse}
                            </span>
                        </p>
                    )}
                    <p style={{ marginBottom: "8px" }}>
                        <b style={{ fontWeight: "bold", fontSize: "14px" }}>
                            Reconnais avoir reçu du {props?.sinistre?.contrat?.organisme?.raisonSociale?.toUpperCase()},
                        </b>
                    </p>
                    <p style={{ marginBottom: "8px", fontSize: "14px" }}>
                        La somme de{" "}
                        <span style={{ fontWeight: "bold", fontSize: "14px", textTransform: "uppercase" }}>
                            {numberToWordsFrench(props?.decaissement?.montantGlobal)} FRANCS CFA
                        </span>
                    </p>
                    <p style={{ marginBottom: "16px", fontSize: "14px" }}>
                        Réprésentant {motifReglementAffiche(props?.decaissement)}.
                    </p>
                    {/* Tableau récapitulatif du règlement groupé : une ligne par
                        règlement avec les infos de SON dossier (même logique que
                        l'ordre de dépense groupé). */}
                    {isGroupe && (
                        <div style={{ marginBottom: "16px" }}>
                            <TableauDossiersGroupe
                                decaissement={props?.decaissement}
                                sinistre={props?.sinistre}
                                fontSize="11px"
                                fontFamily="Calibri, Arial, sans-serif"
                            />
                        </div>
                    )}
                    {/* Formule de conclusion (facultative) choisie à l'édition de la quittance. */}
                    {/* pre-line : les formules de clôture sont en 2 paragraphes (renonciation
                        puis subrogation CIMA art. 42). Sans cela, le saut de ligne est écrasé
                        et les deux clauses se retrouvent collées sur une même ligne. */}
                    {props?.decaissement?.formuleDeFin && (
                        <p style={{ marginBottom: "16px", fontSize: "14px", whiteSpace: "pre-line", textAlign: "justify" }}>
                            {props?.decaissement?.formuleDeFin}
                        </p>
                    )}
                    <p style={{ marginBottom: "16px", fontSize: "14px" }}>
                        Dont quittance sans aucune réserve.
                    </p>
                </div>
                <br />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", fontFamily: "Calibri, Arial, sans-serif" }}>
                    <div className="quittance-text-block" style={{ width: "60%" }}>
                        <p style={{ marginBottom: "6px" }}>
                            <b style={{ fontWeight: "bold", fontSize: "13px" }}>
                                {props?.decaissement?.modePaiement === "VIREMENT"
                                    ? `Virement réf. ${props?.decaissement?.numeroCheque || ""}`
                                    : `Chèque N° ${props?.decaissement?.numeroCheque || ""}`}
                            </b>
                        </p>
                        <p style={{ marginBottom: "6px" }}>
                            <b style={{ fontWeight: "bold", fontSize: "13px" }}>
                                Du{" "}
                                {props?.decaissement?.dateEmissionCheque
                                    ? frenchDateOnly(props?.decaissement?.dateEmissionCheque)
                                    : ""}
                            </b>
                        </p>
                        <p style={{ marginBottom: "6px" }}>
                            <b style={{ fontWeight: "bold", fontSize: "13px" }}>
                                {props?.decaissement?.banque ? props?.decaissement?.banque : ""}
                            </b>
                        </p>
                        <p style={{ marginTop: "20px" }}>
                            <b style={{ fontWeight: "bold", fontSize: "13px" }}>Le ....................................</b>
                        </p>
                    </div>
                    <div className="quittance-text-block" style={{ width: "35%", textAlign: "right" }}>
                        <p style={{ marginBottom: "6px" }}>
                            <b style={{ fontWeight: "bold", fontSize: "13px" }}>
                                A Lomé le, {props?.decaissement?.dateImpressionQuittance
                                    ? frenchDateOnly(props?.decaissement?.dateImpressionQuittance)
                                    : frenchDateOnly(new Date().toISOString())}
                            </b>
                        </p>
                    </div>
                </div>
                {/* VISA ET AUTORISATION. COMPAGNIES : Rédacteur / Chef service sinistre / Directeur des
                    opérations (+ Directeur Général à la demande, persisté). POOL : Rédacteur / Directeur
                    des opérations / Directeur du Pool. */}
                {/* {(() => {
                    const signataires = isCompany
                        ? ["REDACTEUR", "CHEF SERVICE SINISTRE", "DIRECTEUR DES OPERATIONS",
                            ...(dgDemande ? ["DIRECTEUR GENERAL"] : [])]
                        : ["REDACTEUR", "DIRECTEUR DES OPERATIONS", "Directeur du Pool"];
                    const largeur = `${(100 / signataires.length).toFixed(4)}%`;
                    return (
                        <div style={{ marginTop: "18px" }}>
                            <div style={{ textAlign: "center", fontWeight: "bold", textDecoration: "underline", marginBottom: "8px", fontSize: "12px" }}>
                                VISA ET AUTORISATION
                            </div>
                            <table border={1} style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px", color: "black" }}>
                                <tbody>
                                    <tr>
                                        {signataires.map((s) => (
                                            <td key={s} style={{ textAlign: "center", border: "1px solid black", width: largeur, padding: "4px" }}>{s}</td>
                                        ))}
                                    </tr>
                                    <tr>
                                        {signataires.map((s) => (
                                            <td key={s} style={{ border: "1px solid black", width: largeur, padding: "45px 6px 8px" }}></td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    );
                })()} */}
                {/* Signataires PARAMÉTRABLES (aucun par défaut sur la quittance ; le titre
                    n'apparaît que si l'organisme en a configuré). */}
                <div style={{ marginTop: "18px" }}>
                    <SignatairesVisa
                        organismeCode={props?.sinistre?.contrat?.organisme?.code}
                        typeDocument={TYPE_DOC_SIGNATAIRE.QUITTANCE_INDEMNITE}
                        decaissementCodeId={props?.decaissement?.codeId}
                        variant="table"
                        title="VISA ET AUTORISATION"
                        fallback={[]}
                    />
                </div>
                <br />
                <br />
            </div>
                </td></tr></tbody>
            </table>
        </div>
        </>
    );
});

QuittanceIndemnite.propTypes = {
    decaissement: PropTypes.object,
    sinistre: PropTypes.object,
};

export default QuittanceIndemnite;